import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, BrainCircuitIcon, ShieldAlertIcon, HeartPulseIcon, HandIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ReviewsGallery } from '../components/ReviewsGallery';
import { EnrollmentOffer } from '../components/EnrollmentOffer';
import { FAQSection } from '../components/FAQSection';
export function Course3Page() {
  const modules = [{
    id: '01',
    title: 'De ce apar crizele de nervi',
    icon: <BrainCircuitIcon size={48} className="text-brand-gold" data-id="element-679" />,
    bullets: ['De ce copilul tău face crize de nervi și ce încearcă să îți comunice prin ele', 'Diferența dintre un capriciu și o nevoie reală neexprimată', 'Cum să anticipezi și să previi momentele tensionate']
  }, {
    id: '02',
    title: 'Toleranța la frustrare',
    icon: <ShieldAlertIcon size={48} className="text-brand-gold" data-id="element-680" />,
    bullets: ['Cum să îl ajuți să tolereze frustrarea pas cu pas', 'De ce "nu" este atât de greu de acceptat și cum să îl spui corect', 'Cum să gestionezi momentele în care refuză totul']
  }, {
    id: '03',
    title: 'Tehnici de reglare emoțională',
    icon: <HeartPulseIcon size={48} className="text-brand-gold" data-id="element-681" />,
    bullets: ['Tehnici simple de reglare emoțională potrivite vârstei lui', 'Cum să îl ajuți să se calmeze când a pierdut controlul', 'Cum să îți gestionezi propriile emoții în mijlocul crizei']
  }, {
    id: '04',
    title: 'Limite blânde dar ferme',
    icon: <HandIcon size={48} className="text-brand-gold" data-id="element-682" />,
    bullets: ['Cum să pui limite fără să te simți vinovată', 'De ce pedepsele și recompensele nu funcționează pe termen lung', 'Cum să obții cooperarea copilului fără lupte de putere']
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="pt-[64px] md:pt-[72px]" data-id="element-683">
      {/* SECȚIUNEA 1 — HERO OFERTĂ */}
      <section className="bg-brand-navy section-padding text-white" data-id="element-684">
        <div className="container-custom" data-id="element-685">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-686">
            <div className="max-w-xl" data-id="element-687">
              <h1 className="font-playfair text-[48px] md:text-[64px] font-bold text-white mb-6 leading-[1.1]" data-id="element-688">
                Emoții și Reglare
              </h1>
              <p className="font-source text-[20px] md:text-[24px] text-brand-gold mb-10 italic" data-id="element-689">
                Înțelege ce se ascunde în spatele crizelor de nervi și ajută-ți
                copilul să își gestioneze emoțiile, pas cu pas.
              </p>
              <a href="#inscriere" className="btn-inverted text-[18px] px-8 py-4" data-id="element-690">
                Click aici pentru înscriere!
              </a>
            </div>
            <div className="relative rounded-[24px] overflow-hidden shadow-2xl border-4 border-white/10" data-id="element-691">
              <img src="https://assets.cdn.filesafe.space/3SQqBp51ZHIpTGzJ3Vu1/media/6a2bf3330b818c92b366384c.jpg" alt="Emoții și Reglare" className="w-full h-full object-cover aspect-[4/3]" data-id="element-692" />
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 2 — DESCRIERE */}
      <section className="bg-brand-beige section-padding" data-id="element-693">
        <div className="container-custom" data-id="element-694">
          <div className="max-w-3xl mx-auto" data-id="element-695">
            <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy mb-8 text-center" data-id="element-696">
              Nu mai știi ce să faci când copilul tău face crize de nervi din
              orice?
            </h2>
            <div className="font-source text-[16px] md:text-[18px] text-brand-text space-y-6" data-id="element-697">
              <p data-id="element-698">
                Copilul tău face crize de nervi intense. Uneori refuză totul.
                Plânge, țipă, se aruncă pe jos, iar tu nu știi dacă să cedezi,
                să ignori sau să intervii. Te simți depășită, vinovată și
                obosită. Apropiații îți spun "lasă-l în pace" sau "pune-i
                limite", dar nimeni nu îți arată concret cum.
              </p>
              <p data-id="element-699">
                Dacă nu înțelegi ce se întâmplă în spatele acestor
                comportamente, riști să reacționezi în moduri care adâncesc
                problema. Copilul nu face crize ca să te manipuleze. Încercă să
                îți comunice ceva ce nu poate spune cu cuvinte. Iar dacă nu
                primește ghidare acum, aceste comportamente se pot intensifica
                pe măsură ce crește.
              </p>
              <div className="bg-white p-6 rounded-[16px] border-l-4 border-brand-coral" data-id="element-700">
                <p className="font-medium text-brand-navy" data-id="element-701">
                  Cursul "E - Emoții și Reglare" te ajută să înțelegi de ce
                  copilul tău reacționează așa și ce poți face concret. Înveți
                  tehnici simple de reglare emoțională potrivite vârstei lui,
                  cum să pui limite fără vinovăție și cum să faci diferența
                  dintre un capriciu și o nevoie reală neexprimată.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 3 — CE VEI DESCOPERI */}
      <section className="bg-brand-ivory section-padding" data-id="element-702">
        <div className="container-custom" data-id="element-703">
          <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy text-center mb-16" data-id="element-704">
            Ce vei descoperi concret în Cursul 3: Emoții și Reglare
          </h2>
          <div className="max-w-4xl mx-auto space-y-8" data-id="element-705">
            {modules.map(mod => <div key={mod.id} className="flex flex-col md:flex-row bg-white rounded-[24px] shadow-sm border border-gray-100 overflow-hidden p-8 gap-8 items-start" data-id="element-706">
                {/* Icon Side */}
                <div className="flex-shrink-0 w-24 h-24 rounded-full bg-brand-beige flex items-center justify-center" data-id="element-707">
                  {mod.icon}
                </div>

                {/* Content Side */}
                <div className="flex-grow" data-id="element-708">
                  <h3 className="font-playfair text-[24px] md:text-[28px] font-bold text-brand-navy mb-6 leading-[1.2]" data-id="element-709">
                    {mod.title}
                  </h3>
                  <ul className="space-y-4 flex flex-col" data-id="element-710">
                    {mod.bullets.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-711">
                        <CheckCircle2Icon className="text-brand-gold flex-shrink-0 mt-1" size={20} data-id="element-712" />
                        <span className="font-source text-[15px] text-brand-text" data-id="element-713">
                          {item}
                        </span>
                      </li>)}
                  </ul>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 4 — BENEFICII */}
      <section className="bg-brand-beige section-padding" data-id="element-714">
        <div className="container-custom" data-id="element-715">
          <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy text-center mb-12" data-id="element-716">
            Ce se schimbă după ce parcurgi acest curs
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-717">
            {[{
            title: 'Crizele de nervi se reduc în intensitate și frecvență',
            desc: 'Înveți să anticipezi momentele dificile și să intervii înainte ca situația să escaladeze. Copilul învață alternative la plâns și țipat.'
          }, {
            title: 'Știi exact cum să reacționezi',
            desc: 'Nu te mai simți blocată în fața crizelor. Ai un plan clar pentru fiecare tip de situație.'
          }, {
            title: 'Copilul începe să accepte limitele',
            desc: 'Învață că "nu" nu e sfârșitul lumii. Tolerează mai bine frustrarea și se adaptează mai ușor la schimbări.'
          }, {
            title: 'Nu te mai simți vinovată',
            desc: 'Înțelegi că a pune limite nu înseamnă că ești o mamă rea. Și că a fi caldă nu înseamnă că ești slabă.'
          }].map((card, i) => <div key={i} className="bg-white p-6 rounded-[16px] shadow-sm border-t-4 border-brand-gold" data-id="element-718">
                <h3 className="font-montserrat font-bold text-[16px] text-brand-navy mb-3" data-id="element-719">
                  {card.title}
                </h3>
                <p className="font-source text-[14px] text-brand-text" data-id="element-720">
                  {card.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 5 — CINE SUNT EU */}
      <section className="bg-brand-ivory section-padding" data-id="element-721">
        <div className="container-custom" data-id="element-722">
          <div className="max-w-4xl mx-auto" data-id="element-723">
            <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy text-center mb-10" data-id="element-724">
              Cine sunt eu și de ce am creat acest curs
            </h2>
            <div className="flex flex-col-reverse md:grid md:grid-cols-3 gap-8 items-stretch" data-id="element-725">
              <div className="md:col-span-2 font-source text-[16px] md:text-[18px] text-brand-text space-y-4 text-center md:text-left flex flex-col justify-center" data-id="element-726">
                <p data-id="element-727">
                  Sunt Cristina Geană, psiholog clinician specializat în
                  dezvoltarea copilului mic. Am lucrat cu sute de copii care
                  aveau comportamente explozive, refuz, crize intense. Și am
                  văzut cum totul se schimbă când mama înțelege ce se întâmplă
                  și primește instrumente concrete.
                </p>
                <p data-id="element-728">
                  Am creat acest curs pentru că știu cât de singure și de
                  judecate se simt mamele care au copii "dificili". Și pentru că
                  soluția nu e nici să cedezi, nici să forțezi, ci să înțelegi.
                </p>
              </div>
              <div className="md:col-span-1" data-id="element-729">
                <img src="https://cdn.magicpatterns.com/uploads/wvUBHAy6VhqaRqNiVJPB6X/Cristina_poze_%285%29.jpg" alt="Cristina Geană" className="w-full h-full object-cover object-top rounded-[24px] shadow-card" data-id="element-730" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 6 — PENTRU CINE ESTE / NU ESTE */}
      <section className="bg-brand-navy section-padding text-white" data-id="element-731">
        <div className="container-custom" data-id="element-732">
          <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-white text-center mb-12" data-id="element-733">
            Pentru cine este acest curs?
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-734">
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-[24px] border border-white/20" data-id="element-735">
              <h3 className="font-playfair text-[28px] font-bold text-brand-gold mb-6 !text-brand-gold" data-id="element-736">
                Pentru cine este
              </h3>
              <ul className="space-y-4" data-id="element-737">
                {['Mame care se simt copleșite de crizele de nervi ale copilului', 'Mame care vor să învețe cum să pună limite fără pedepse și țipete', 'Mame de copii care nu tolerează frustrarea și refuză orice propunere'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-738">
                    <CheckCircle2Icon className="text-brand-gold flex-shrink-0 mt-0.5" size={20} data-id="element-739" />
                    <span className="font-source text-[15px] text-white/90" data-id="element-740">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
            <div className="bg-white/5 p-8 rounded-[24px] border border-white/10" data-id="element-741">
              <h3 className="font-playfair text-[28px] font-bold text-white mb-6 !text-white" data-id="element-742">
                Pentru cine NU este
              </h3>
              <ul className="space-y-4" data-id="element-743">
                {['Mame care caută o soluție magică prin care copilul să nu mai plângă niciodată', 'Mame care nu sunt dispuse să își schimbe propria reacție în fața crizelor copilului', 'Mame ai căror copii au tulburări psihiatrice diagnosticate care necesită intervenție medicală'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-744">
                    <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-745">
                      <span className="w-1.5 h-1.5 rounded-full bg-white/60" data-id="element-746"></span>
                    </div>
                    <span className="font-source text-[15px] text-white/70" data-id="element-747">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 7 — OFERTĂ */}
      <EnrollmentOffer title="Ce primești și care este investiția ta" includes={['Acces pe viață la Modulul 3 - Emoții și Reglare', 'Materiale video structurate pas cu pas', 'Tehnici practice de aplicat acasă', 'Informație clară, fără termeni medicali']} price="395 lei" note="Acest modul face parte din Colecția Completă Uite cum C.R.E.S.C., dar poate fi achiziționat și separat." data-id="element-748" />

      {/* SECȚIUNEA 8 — TESTIMONIALE */}
      <section className="bg-brand-ivory section-padding" data-id="element-749">
        <div className="container-custom" data-id="element-750">
          <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy text-center mb-12" data-id="element-751">
            Ce spun mamele care deja au lucrat cu mine
          </h2>
          <ReviewsGallery data-id="element-752" />
        </div>
      </section>

      {/* SECȚIUNEA 9 — FAQ */}
      <FAQSection faqs={[{
      q: 'Când primesc acces la curs?',
      a: 'Imediat după confirmarea plății, vei primi pe email datele de acces la platforma unde se află cursul.'
    }, {
      q: 'Cât timp am acces la materiale?',
      a: 'Ai acces pe viață la acest modul. Îl poți revedea ori de câte ori ai nevoie, în ritmul tău.'
    }, {
      q: 'Pot cumpăra și restul modulelor mai târziu?',
      a: 'Da, poți achiziționa celelalte module separat sau poți opta pentru Colecția Completă C.R.E.S.C. pentru a beneficia de prețul special și de apelurile de grup.'
    }]} data-id="element-753" />

      {/* SECȚIUNEA 10 — ÎNSCRIERE */}
      <section id="inscriere" className="bg-brand-navy section-padding text-center text-white" data-id="element-754">
        <div className="container-custom" data-id="element-755">
          <div className="max-w-3xl mx-auto" data-id="element-756">
            <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-white mb-6" data-id="element-757">
              Înscriere Modulul 3 - Emoții și Reglare
            </h2>
            <p className="font-source text-[18px] text-white/80 mb-8" data-id="element-758">
              Completează formularul de mai jos pentru a finaliza înscrierea.
            </p>

            <div className="bg-white rounded-[24px] overflow-hidden min-h-[600px]" data-id="element-759">
              <iframe src="https://api.leadconnectorhq.com/widget/form/qdY6dZtBtHJPNJM09Ud0" style={{
              width: '100%',
              height: '100%',
              minHeight: '600px',
              border: 'none'
            }} id="inline-qdY6dZtBtHJPNJM09Ud0" data-layout="{'id':'INLINE'}" data-trigger-type="alwaysShow" data-trigger-value="" data-activation-type="alwaysActivated" data-activation-value="" data-deactivation-type="neverDeactivate" data-deactivation-value="" data-form-name="Form Modul 3" data-height="600" data-layout-iframe-id="inline-qdY6dZtBtHJPNJM09Ud0" data-form-id="qdY6dZtBtHJPNJM09Ud0" title="Form Modul 3" data-id="element-760" />
            </div>
          </div>
        </div>
      </section>
    </motion.div>;
}